import { useState } from "react";
import { RULE_TYPES, evalRules } from "../../lib/rules.js";
import { I } from "../Icons.jsx";
import Btn from "../ui/Btn.jsx";
import Display from "../ui/Display.jsx";
import Eyebrow from "../ui/Eyebrow.jsx";
import IconBtn from "../ui/IconBtn.jsx";
import { inputStyle, selectStyle } from "../ui/styles.js";

export default function RulesOverlay({ rules, people, activities, gtypes, onAdd, onDelete, onClose }) {
  const [personA, setPersonA] = useState("");
  const [personB, setPersonB] = useState("");
  const [type, setType] = useState("never_with");
  const [count, setCount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(() => gtypes.map(g => g.value));

  const rt = RULE_TYPES.find(r => r.value === type);
  const nameOf = (pid) => people.find(p => p.id === pid)?.Name || "?";
  const evals = evalRules(rules, activities);
  const sortedPeople = [...people].sort((a, b) => a.Name.localeCompare(b.Name));

  const submit = async () => {
    setError(null);
    if (!personA || !personB) return setError("Pick both people.");
    if (personA === personB) return setError("Same person twice.");
    if (rt.needsCount) {
      const n = parseInt(count);
      if (!(n >= 0)) return setError("Enter a non-negative count.");
    }
    if (selectedTypes.length === 0) return setError("Pick at least one activity type.");
    const allSelected = selectedTypes.length === gtypes.length;
    setBusy(true);
    try {
      await onAdd({
        personA,
        personB,
        type,
        count: rt.needsCount ? parseInt(count) : null,
        activityTypes: allSelected ? null : selectedTypes,
      });
      setPersonA(""); setPersonB(""); setCount("");
      setSelectedTypes(gtypes.map(g => g.value));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleType = (v) => setSelectedTypes(prev =>
    prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
  );

  const describe = (r) => {
    const rtype = RULE_TYPES.find(t => t.value === r.type);
    return `${rtype?.label}${rtype?.needsCount ? ` (${r.count})` : ""}`;
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(34,28,20,0.4)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--paper-0)", borderRadius: 14, padding: 28,
        width: "92%", maxWidth: 640, maxHeight: "86vh", overflow: "auto",
        border: "1px solid var(--line)", boxShadow: "0 24px 64px rgba(34,28,20,0.25)",
        color: "var(--ink)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <Eyebrow>Placement rules</Eyebrow>
            <Display size={24} style={{ marginTop: 8 }}>Quiet constraints</Display>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
              Pairings to keep together or apart. Applied during auto-fill and flagged when broken.
            </div>
          </div>
          <IconBtn icon={I.x} title="Close" onClick={onClose} />
        </div>

        {/* Add rule */}
        <div style={{
          padding: 16, borderRadius: 10,
          border: "1px solid var(--line-2)", background: "var(--paper-1)",
          marginBottom: 18,
        }}>
          <Eyebrow style={{ marginBottom: 10 }}>Add rule</Eyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.9fr) minmax(0,1fr)", gap: 8 }}>
            <select value={personA} onChange={(e) => setPersonA(e.target.value)} style={selectStyle}>
              <option value="">Person A…</option>
              {sortedPeople.map(p => <option key={p.id} value={p.id}>{p.Name}</option>)}
            </select>
            <select value={type} onChange={(e) => setType(e.target.value)} style={selectStyle}>
              {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select value={personB} onChange={(e) => setPersonB(e.target.value)} style={selectStyle}>
              <option value="">Person B…</option>
              {sortedPeople.map(p => <option key={p.id} value={p.id}>{p.Name}</option>)}
            </select>
          </div>
          {rt?.needsCount && (
            <input
              type="number"
              min="0"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder="N"
              style={{ ...inputStyle, width: 100, marginTop: 6 }}
            />
          )}
          <div style={{ marginTop: 12 }}>
            <Eyebrow style={{ marginBottom: 6 }}>
              Applies to {selectedTypes.length === gtypes.length ? "all activity types" : `${selectedTypes.length} of ${gtypes.length} types`}
            </Eyebrow>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {gtypes.map(g => {
                const on = selectedTypes.includes(g.value);
                const Icon = g.Icon || I.other;
                return (
                  <button
                    key={g.value}
                    type="button"
                    title={g.label}
                    onClick={() => toggleType(g.value)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "5px 10px", borderRadius: 7,
                      border: `1px solid ${on ? "var(--ink)" : "var(--line-2)"}`,
                      background: on ? "var(--ink)" : "transparent",
                      color: on ? "var(--paper-0)" : "var(--ink-3)",
                      fontSize: 11.5,
                      fontWeight: 500,
                      cursor: "pointer",
                      fontFamily: "var(--ui)",
                      transition: "all 0.12s",
                    }}
                  >
                    <Icon size={12} stroke="currentColor" />
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>
          {error && (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--warn-ink)" }}>{error}</div>
          )}
          <div style={{ marginTop: 14 }}>
            <Btn variant="primary" size="sm" onClick={submit} disabled={busy}>
              {busy ? "Adding…" : "Add rule"}
            </Btn>
          </div>
        </div>

        <Eyebrow style={{ marginBottom: 10 }}>Existing rules · {rules.length}</Eyebrow>
        {rules.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>No rules yet.</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rules.map(r => {
            const ev = evals.find(e => e.rule.id === r.id);
            const isTogether = r.type === "always_with" || r.type === "at_least";
            const broken = ev?.broken;
            return (
              <div
                key={r.id}
                style={{
                  padding: "14px 16px", borderRadius: 10,
                  border: `1px solid ${broken ? "var(--warn-line)" : "var(--line-2)"}`,
                  background: broken ? "var(--warn-soft)" : "var(--paper-1)",
                  display: "flex", alignItems: "center", gap: 14,
                }}
              >
                <div style={{
                  padding: "3px 9px", borderRadius: 4,
                  fontSize: 10.5, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase",
                  background: isTogether ? "var(--accent-soft)" : "var(--paper-2)",
                  color: isTogether ? "var(--accent)" : "var(--ink-2)",
                  border: `1px solid ${isTogether ? "var(--accent)" : "var(--line-2)"}`,
                  flexShrink: 0,
                }}>{isTogether ? "Together" : "Apart"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, color: "var(--ink)" }}>
                    <strong>{nameOf(r.person_a)}</strong>{" "}
                    <span style={{ color: "var(--ink-3)" }}>{describe(r)}</span>{" "}
                    <strong>{nameOf(r.person_b)}</strong>
                  </div>
                  <div style={{ fontSize: 11.5, color: broken ? "var(--warn-ink)" : "var(--ink-3)", marginTop: 2 }}>
                    {(!r.activity_types || r.activity_types.length === 0) ? "Everywhere" : `During ${r.activity_types.join(", ")}`}
                    {ev && <> · {ev.reason}</>}
                  </div>
                </div>
                <IconBtn
                  icon={I.trash}
                  title="Remove"
                  onClick={() => {
                    if (confirm(`Delete this rule?\n\n${nameOf(r.person_a)} ${describe(r)} ${nameOf(r.person_b)}`)) {
                      onDelete(r.id);
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
